import React, { useState } from 'react';
import { ClipboardList, ChevronDown, ChevronUp } from 'lucide-react';
import { useMarket } from '../context/MarketContext';
import type { Order } from '../types';
import { toEAT } from '../utils/time';
import ProgressBar from '../components/ProgressBar';
import FillHistoryTable from '../components/FillHistoryTable';
import { INSTRUMENTS } from '../constants/instruments';

function formatTimestamp(time: string): string {
  if (!time) return '—';
  try {
    const date = new Date(time);
    if (!isNaN(date.getTime())) {
      return toEAT(date);
    }
    return time;
  } catch {
    return time;
  }
}

export default function OrdersPage() {
  const { orders, selectedSymbol, labelForSecId } = useMarket();
  const [expandedOrders, setExpandedOrders] = useState<Set<string>>(new Set());
  
  const toggleExpand = (orderId: string) => {
    setExpandedOrders(prev => {
      const next = new Set(prev);
      if (next.has(orderId)) {
        next.delete(orderId);
      } else {
        next.add(orderId);
      }
      return next;
    });
  };
  
  // Helper function to convert symbol (label or secId) to secId
  const symbolToSecId = (symbol: string): string => {
    const upperSymbol = (symbol || '').toUpperCase();
    
    // Check if it's already a secId
    const isSecId = INSTRUMENTS.some(inst => inst.secId.toUpperCase() === upperSymbol);
    if (isSecId) {
      return upperSymbol;
    }
    
    // Check if it's a label and convert to secId
    const instrument = INSTRUMENTS.find(inst => inst.label.toUpperCase() === upperSymbol);
    if (instrument) {
      return instrument.secId.toUpperCase();
    }
    
    // If not found, return as-is (might be a different format)
    return upperSymbol;
  };
  
  // Filter orders by selected symbol (secId)
  const selectedSecId = (selectedSymbol || '').toUpperCase();
  const filteredOrders = orders.filter(order => {
    const orderSecId = symbolToSecId(order.symbol || '');
    return orderSecId === selectedSecId;
  });
  
  const card = { background:'#1e293b', padding:16, borderRadius:8, border:'1px solid #334155' };
  const symbolLabel = labelForSecId(selectedSymbol);
  
  return (
    <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
      <div style={card}>
        <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:12 }}>
          <ClipboardList size={16} color="#94a3b8"/>
          <h3 style={{ margin:0, fontSize:16, fontWeight:600 }}>
            Orders {selectedSymbol && `- ${symbolLabel} (${selectedSymbol})`}
          </h3>
        </div>
        {filteredOrders.length === 0 ? (
          <div style={{ color:'#94a3b8', fontSize:13 }}>
            {orders.length === 0 
              ? 'No orders yet.' 
              : `No orders for ${symbolLabel} (${selectedSymbol}).`}
          </div>
        ) : (
          <>
            <Header/>
            <div style={{ maxHeight:520, overflowY:'auto' }}>
              {filteredOrders.map(o => (
                <OrderRow 
                  key={o.id} 
                  order={o} 
                  isExpanded={expandedOrders.has(o.id)}
                  onToggleExpand={() => toggleExpand(o.id)}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Header() {
  return (
    <div style={{
      display:'grid', 
      gridTemplateColumns:'140px 100px 80px 1fr 1fr 1fr 120px 140px 40px',
      gap:8, 
      padding:'8px 0', 
      color:'#94a3b8', 
      fontSize:12, 
      borderBottom:'1px solid #334155'
    }}>
      <div>Order ID</div>
      <div>Symbol</div>
      <div>Side</div>
      <div style={{ textAlign:'right' }}>Original</div>
      <div style={{ textAlign:'right' }}>Filled</div>
      <div style={{ textAlign:'right' }}>Remaining</div>
      <div>Status</div>
      <div>Time</div>
      <div></div>
    </div>
  );
}

interface OrderRowProps {
  order: Order;
  isExpanded: boolean;
  onToggleExpand: () => void;
}

function OrderRow({ order, isExpanded, onToggleExpand }: OrderRowProps) {
  const statusColors: Record<string, string> = {
    'new': '#3b82f6',
    'filled': '#10b981',
    'partially_filled': '#f59e0b',
    'rejected': '#ef4444',
    'cancelled': '#94a3b8'
  };
  
  const filledQty = order.cumQty ?? 0;
  const remainingQty = order.leavesQty ?? (order.qty - filledQty);
  const fillPercentage = order.qty > 0 ? (filledQty / order.qty) * 100 : 0;
  
  return (
    <>
      <div 
        style={{
          display:'grid', 
          gridTemplateColumns:'140px 100px 80px 1fr 1fr 1fr 120px 140px 40px',
          gap:8, 
          padding:'10px 0', 
          borderBottom:'1px solid #334155',
          cursor: 'pointer',
          transition: 'background-color 0.2s'
        }}
        onClick={onToggleExpand}
        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#0f172a'}
        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
      >
        <div style={{ fontFamily:'monospace', fontSize:11 }}>
          {order.id.length > 20 ? order.id.substring(0, 20) + '...' : order.id}
        </div>
        <div style={{ fontWeight:500 }}>{order.symbol}</div>
        <div style={{ 
          color: order.side==='buy' ? '#10b981' : '#ef4444', 
          fontWeight:600 
        }}>
          {order.side.toUpperCase()}
        </div>
        <div style={{ textAlign:'right', fontWeight:500 }}>
          {order.qty.toLocaleString()}
        </div>
        <div style={{ textAlign:'right', fontWeight:500, color: '#e2e8f0' }}>
          {filledQty.toLocaleString()}/{order.qty.toLocaleString()}
        </div>
        <div style={{ textAlign:'right', fontWeight:500, color: '#94a3b8' }}>
          {remainingQty.toLocaleString()}
        </div>
        <div>
          <span style={{ 
            color: statusColors[order.status] || '#94a3b8',
            fontWeight: 600,
            fontSize: 11,
            textTransform: 'capitalize'
          }}>
            {order.status.replace('_',' ')}
            {order.status === 'partially_filled' && ` (${fillPercentage.toFixed(0)}%)`}
          </span>
        </div>
        <div style={{ fontSize:11, color:'#94a3b8' }}>
          {formatTimestamp(order.time)}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {isExpanded ? (
            <ChevronUp size={16} color="#94a3b8" />
          ) : (
            <ChevronDown size={16} color="#94a3b8" />
          )}
        </div>
      </div>
      
      {isExpanded && (
        <div style={{
          padding: '16px',
          backgroundColor: '#0f172a',
          borderBottom: '1px solid #334155',
          borderLeft: '3px solid',
          borderLeftColor: statusColors[order.status] || '#94a3b8'
        }}>
          <div style={{ marginBottom: 12 }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: 8
            }}>
              <div style={{ fontSize: 12, color: '#94a3b8', fontWeight: 600 }}>
                Fill Progress
              </div>
              <div style={{ fontSize: 11, color: '#64748b' }}>
                {filledQty.toLocaleString()} / {order.qty.toLocaleString()} ({fillPercentage.toFixed(1)}%)
              </div>
            </div>
            <ProgressBar 
              filled={filledQty} 
              total={order.qty} 
              status={order.status} 
            />
          </div>
          
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: '1fr 1fr 1fr', 
            gap: 12, 
            marginBottom: 16,
            fontSize: 12
          }}>
            <div>
              <div style={{ color: '#64748b', marginBottom: 4 }}>Limit Price</div>
              <div style={{ color: '#e2e8f0', fontWeight: 600 }}>
                {order.price > 0 ? order.price.toFixed(2) : '—'}
              </div>
            </div>
            {order.avgFillPrice !== undefined && (
              <div>
                <div style={{ color: '#64748b', marginBottom: 4 }}>Avg Fill Price</div>
                <div style={{ color: '#e2e8f0', fontWeight: 600 }}>
                  {order.avgFillPrice.toFixed(2)}
                </div>
              </div>
            )}
            {order.lastFillTime && (
              <div>
                <div style={{ color: '#64748b', marginBottom: 4 }}>Last Fill</div>
                <div style={{ color: '#e2e8f0', fontWeight: 600, fontSize: 11 }}>
                  {formatTimestamp(order.lastFillTime)}
                </div>
              </div>
            )}
          </div>
          
          {order.fills && order.fills.length > 0 && (
            <FillHistoryTable fills={order.fills} order={order} />
          )}
          
          {(!order.fills || order.fills.length === 0) && (
            <div style={{ 
              padding: '16px', 
              textAlign: 'center', 
              color: '#94a3b8', 
              fontSize: 13,
              backgroundColor: '#1e293b',
              borderRadius: 6,
              border: '1px solid #334155'
            }}>
              No fills recorded yet
            </div>
          )}
        </div>
      )}
    </>
  );
}
